import { DOCUMENT } from '@angular/common';
import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { FUSE_VERSION } from '@fuse/version';
import { Subject, combineLatest, filter, map, takeUntil } from 'rxjs';
import { SettingsComponent } from './common/settings/settings.component';
import { EmptyLayoutComponent } from './layouts/empty/empty.component';
import { CenteredLayoutComponent } from './layouts/horizontal/centered/centered.component';
import { EnterpriseLayoutComponent } from './layouts/horizontal/enterprise/enterprise.component';
import { MaterialLayoutComponent } from './layouts/horizontal/material/material.component';
import { ModernLayoutComponent } from './layouts/horizontal/modern/modern.component';
import { ClassicLayoutComponent } from './layouts/vertical/classic/classic.component';
import { ClassyLayoutComponent } from './layouts/vertical/classy/classy.component';
import { CompactLayoutComponent } from './layouts/vertical/compact/compact.component';
import { DenseLayoutComponent } from './layouts/vertical/dense/dense.component';
import { FuturisticLayoutComponent } from './layouts/vertical/futuristic/futuristic.component';
import { ThinLayoutComponent } from './layouts/vertical/thin/thin.component';
import { MobileLayoutComponent } from './layouts/mobile/mobile.component';
import { KioskLayoutComponent } from './layouts/kiosk/kiosk.component';
import { FuseNavigationService } from '@fuse/components/navigation';
import { MailboxService } from 'app/modules/admin/mailbox/mailbox.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MobileDetectionService } from 'app/core/services/mobile-detection.service';
import { KioskService } from 'app/core/services/kiosk.service';
import { VoiceService } from 'app/core/services/voice.service';
import { VideoCallService } from 'app/core/services/video-call.service';
import { VideoCallDrawerComponent } from './common/video-call-drawer/video-call-drawer.component';
import { McpRealtimeRouterService } from 'app/core/services/realtime-segments/mcp-realtime-router.service';
import { QuickChatVideoCallService } from 'app/core/services/quick-chat-video-call.service';
@Component({
    selector: 'layout',
    standalone: true,
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        EmptyLayoutComponent,
        CenteredLayoutComponent,
        EnterpriseLayoutComponent,
        MaterialLayoutComponent,
        ModernLayoutComponent,
        ClassicLayoutComponent,
        ClassyLayoutComponent,
        CompactLayoutComponent,
        DenseLayoutComponent,
        FuturisticLayoutComponent,
        ThinLayoutComponent,
        MobileLayoutComponent,
        KioskLayoutComponent,
        SettingsComponent,
        VideoCallDrawerComponent,
    ],
})
export class LayoutComponent implements OnInit, OnDestroy {
    config: FuseConfig;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;
    isAdmin: boolean = false;
    // Traders get a trimmed Settings drawer (theme color + landing
    // workspace), so they need the drawer rendered too — see layout.html.
    isTrader: boolean = false;
    // Sales get the same trimmed Settings drawer as Traders, so the
    // drawer must be mounted for them as well — see layout.html.
    isSales: boolean = false;
    count: number = 0;
    readonly videoCallsEnabled: boolean;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private _document: any,
        private _renderer2: Renderer2,
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fusePlatformService: FusePlatformService,
        private _fuseNavigationService: FuseNavigationService,
        private _mailboxService: MailboxService,
        private _snackBar: MatSnackBar,
        private _mobileDetectionService: MobileDetectionService,
        private _kioskService: KioskService,
        private _voiceService: VoiceService,
        private _videoCallService: VideoCallService,
        private _quickChatVideoCallService: QuickChatVideoCallService,
        private _mcpRealtime: McpRealtimeRouterService
    ) {
        this.videoCallsEnabled = this._videoCallService.enabled;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Set the theme and scheme based on the configuration
        combineLatest([
            this._fuseConfigService.config$,
            this._fuseMediaWatcherService.onMediaQueryChange$([
                '(prefers-color-scheme: dark)',
                '(prefers-color-scheme: light)',
            ]),
        ])
            .pipe(
                takeUntil(this._unsubscribeAll),
                map(([config, mql]) => {
                    const options = {
                        scheme: config.scheme,
                        theme: config.theme,
                    };

                    // If the scheme is set to 'auto'...
                    if (config.scheme === 'auto') {
                        // Decide the scheme using the media query
                        options.scheme = mql.breakpoints[
                            '(prefers-color-scheme: dark)'
                        ]
                            ? 'dark'
                            : 'light';
                    }

                    return options;
                })
            )
            .subscribe((options) => {
                // Store the options
                this.scheme = options.scheme;
                this.theme = options.theme;

                // Update the scheme and theme
                this._updateScheme();
                this._updateTheme();
            });

        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) => {
                // Store the config
                this.config = config;

                // Update the layout
                this._updateLayout();
            });

        // Apply saved theme from database (userData) on startup
        this._applyUserTheme();

        // Subscribe to NavigationEnd event
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                // Update the layout
                this._updateLayout();
            });

        // Set the app version
        this._renderer2.setAttribute(
            this._document.querySelector('[ng-version]'),
            'fuse-version',
            FUSE_VERSION
        );

        // Set the OS name
        this._renderer2.addClass(
            this._document.body,
            this._fusePlatformService.osName
        );

        // Kiosk lock — keep a kiosk display on the /kiosk surface when kiosk
        // mode is enabled. Runs before the mobile redirect so it takes
        // precedence.
        this._handleKioskRedirect();

        // Mobile device auto-redirect
        // Redirect mobile users to mobile routes if not already on mobile routes
        // and not on auth pages (sign-in, sign-up, etc.)
        this._handleMobileRedirect();

        // Start global webhook polling for email updates (runs even when not on mailbox page)
        setTimeout(() => {
            try {
                const userData = JSON.parse(localStorage.getItem('userData') || '{}');
                if (userData?.email_token) {
                    this._mailboxService.startWebhookPolling();
                    
                    // Subscribe to new email notifications from webhook (more reliable)
                    console.log('📬 [Layout] Subscribing to newEmailsDetected$');
                    this._mailboxService.newEmailsDetected$
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(({ count, emails }) => {
                            console.log(`📬 [Layout] Received newEmailsDetected: ${count} new email(s), mailboxActive=${this._mailboxService.isMailboxCurrentlyActive()}`);
                            
                            // Show notification (even if on mailbox - user might not notice the new email)
                            console.log('📬 [Layout] Showing snackbar notification!');
                            const message = count === 1 
                                ? `📬 1 new email received`
                                : `📬 ${count} new emails received`;
                                
                            const snackBarRef = this._snackBar.open(
                                message, 
                                'View', 
                                { 
                                    duration: 6000,
                                    verticalPosition: 'top',
                                    panelClass: ['fiery-snackbar']
                                }
                            );
                            
                            // Navigate to mailbox when "View" is clicked
                            snackBarRef.onAction().subscribe(() => {
                                this._router.navigate(['/mailbox/inbox/1']);
                            });
                        });
                }
            } catch {}
        }, 3000);

        // Initialize Voice service for incoming calls (Admin, Sales, Trader with voice number)
        setTimeout(() => {
            try {
                const userData = JSON.parse(localStorage.getItem('userData') || '{}');
                // Production / Printer use schedule COMMS Call on workspace + kiosk.
                const allowedRoles = ['Admin', 'Sales', 'Trader', 'Assistant', 'Production', 'Printer'];
                if (userData?.id && allowedRoles.includes(userData?.role)) {
                    console.log('📞 [Layout] Initializing Voice service...');
                    this._voiceService.initialize().then(() => {
                        console.log('📞 [Layout] Voice service initialized');
                    }).catch(err => {
                        // Silently fail if voice not enabled for user
                        console.log('📞 [Layout] Voice not available:', err.message);
                    });
                }
            } catch {}
        }, 5000);

        if (
            this._videoCallService.enabled
            && !this._router.url.split(/[?#]/)[0].startsWith('/kiosk')
        ) {
            try {
                const userData = JSON.parse(localStorage.getItem('userData') || '{}');
                const allowedRoles = ['Admin', 'Sales', 'Trader', 'Assistant', 'Production', 'Printer'];
                if (userData?.id && allowedRoles.includes(userData?.role)) {
                    this._videoCallService.startPollingForIncomingCalls();
                }
            } catch {
                // Public and malformed sessions do not initialize authenticated polling.
            }
        }

        if (
            this._quickChatVideoCallService.enabled
            && !this._router.url.split(/[?#]/)[0].startsWith('/kiosk')
        ) {
            try {
                const userData = JSON.parse(localStorage.getItem('userData') || '{}');
                const allowedRoles = ['Admin', 'Etsy_Admin', 'Sales', 'Trader', 'Assistant', 'Production', 'Printer'];
                if (userData?.id && allowedRoles.includes(userData?.role)) {
                    this._quickChatVideoCallService.startIncomingPolling();
                }
            } catch {
                // Invalid local session data cannot initialize authenticated polling.
            }
        }

        // Global MCP prewarm so first wake/command is faster across layouts.
        setTimeout(() => {
            if (!this._shouldPrewarmMcp()) {
                return;
            }
            this._mcpRealtime.prewarm().catch((err) => {
                console.log('[MCP] Global prewarm skipped:', err?.message ?? err);
            });
        }, 1500);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._mailboxService.stopWebhookPolling();
        this._videoCallService.stopPollingForIncomingCalls();
        this._quickChatVideoCallService.stopIncomingPolling();
        
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the selected layout
     */
    private _updateLayout(): void {
  
        // Get the current activated route        
        let route = this._activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        
        // 1. Set the layout from the config
        this.layout = this.config.layout;   
        let userData: any = null;
        const userDataString = localStorage.getItem('userData');
        if (userDataString) {
            try {

                userData = JSON.parse(userDataString);
            } catch (e) {
                console.error('Error parsing userData from localStorage:', e);
            }
        }else{ 
            this.config.layout = 'dense';
        }
         
        let newLayout = this.config.layout;

        

        if(userData){
            const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
            if (layoutFromQueryParam) {
                newLayout = layoutFromQueryParam;
            }
             
            switch (userData.role) {
                case 'Admin': 
                    // Check for saved layout preference from database (userData)
                    const dbLayoutPreference = userData?.layout_preference || userData?.layoutPreference;
                    
                    // Use database preference if available, otherwise use config layout
                    if (dbLayoutPreference && (dbLayoutPreference === 'modern' || dbLayoutPreference === 'dense')) {
                        this.layout = dbLayoutPreference;
                        this.config.layout = dbLayoutPreference;
                    } else {
                        // Fallback to config layout if no valid preference found
                        this.layout = this.config.layout || 'dense';
                        this.config.layout = this.config.layout || 'dense';
                    }
                    this.isAdmin = true;
                    break;
                case 'Production':
                    // Production users get modern layout (horizontal nav) - limited admin role
                    this.layout = 'modern';
                    this.config.layout = 'modern';
                    // Production users need access to settings drawer (for their work page assignment)
                    this.isAdmin = true;
                    break;
                case 'Trader':
                    this.layout = 'modern';
                    this.config.layout = 'modern';
                    // Trader gets the trimmed Settings drawer.
                    this.isTrader = true;
                    break;
                case 'Assistant':
                    this.layout = 'modern';
                    this.config.layout = 'modern';
                    // Mirror parent org: Trader-assistant vs Sales-assistant
                    try {
                        const ud = JSON.parse(localStorage.getItem('userData') || '{}');
                        if (ud?.operates_as_trader) {
                            this.isTrader = true;
                        } else {
                            this.isSales = true;
                        }
                    } catch {
                        this.isSales = true;
                    }
                    break;
                case 'Sales':
                    this.layout = 'modern';
                    this.config.layout = 'modern';
                    // Sales gets the trimmed Settings drawer (theme + landing
                    // workspace), same as Trader.
                    this.isSales = true;
                    break;
                case 'Customer':
                    this.layout = 'modern';
                    this.config.layout = 'modern';
                    break;
            }
            
        }else{
            this.layout = this.config.layout;
        }  

        // 2. Get the query parameter from the current route and
        // set the layout and save the layout to the config
        const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
        if (layoutFromQueryParam) {
            this.layout = layoutFromQueryParam;
            if (this.config) {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Iterate through the paths and change the layout as we find
        // a config for it.
        //
        // The reason we do this is that there might be empty grouping
        // paths or componentless routes along the path. Because of that,
        // we cannot just assume that the layout configuration will be
        // in the last path's config or in the first path's config.
        //
        // So, we get all the paths that matched starting from root all
        // the way to the current activated route, walk through them one
        // by one and change the layout as we find the layout config. This
        // way, layout configuration can live anywhere within the path and
        // we won't miss it.
        //
        // Also, this will allow overriding the layout in any time so we
        // can have different layouts for different routes.
        const paths = route.pathFromRoot;
        paths.forEach((path) => {
            // Check if there is a 'layout' data
            if (
                path.routeConfig &&
                path.routeConfig.data &&
                path.routeConfig.data.layout
            ) {
                // Set the layout
                this.layout = path.routeConfig.data.layout;
            }
        });

        // 4. Switch printer view to fullscreen only for printer role
        const currentUrl = this._router.url ?? '';
        const isPrinterViewRoute = currentUrl.includes('/printer-view');
        if (isPrinterViewRoute) {
            if (userData?.role === 'Printer') {
                this.layout = 'empty';
            } else if (this.layout === 'empty') {
                this.layout = this.config.layout;
            }
        }
    }

    /**
     * Apply saved theme from database (userData) on startup
     *
     * @private
     */
    private _applyUserTheme(): void {
        // Get user data from localStorage
        const userDataString = localStorage.getItem('userData');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                // Check for saved theme color from database
                const dbThemeColor = userData?.theme_color || userData?.themeColor;
                
                // Apply saved theme if available and different from current
                if (dbThemeColor && this.config && this.config.theme !== dbThemeColor) {
                    this._fuseConfigService.config = { theme: dbThemeColor };
                }
            } catch (e) {
                console.error('Error parsing userData for theme:', e);
            }
        }
    }

    /**
     * Update the selected scheme
     *
     * @private
     */
    private _updateScheme(): void {
        // Remove class names for all schemes
        this._document.body.classList.remove('light', 'dark');

        // Add class name for the currently selected scheme
        this._document.body.classList.add(this.scheme);
    }

    /**
     * Update the selected theme
     *
     * @private
     */
    private _updateTheme(): void {
        // Find the class name for the previously selected theme and remove it
        this._document.body.classList.forEach((className: string) => {
            if (className.startsWith('theme-')) {
                this._document.body.classList.remove(
                    className,
                    className.split('-')[1]
                );
            }
        });

        // Add class name for the currently selected theme
        this._document.body.classList.add(this.theme);
    }

    /**
     * Handle mobile device redirect
     * Redirects mobile users to mobile-optimized routes automatically
     *
     * @private
     */
    private _handleMobileRedirect(): void {
        // Check if on mobile device
        if (!this._mobileDetectionService.isMobile) {
            return;
        }

        // Get current URL
        const currentUrl = this._router.url;
        
        // Skip redirect for:
        // - Already on mobile routes (/m/)
        // - Auth pages (sign-in, sign-up, sign-out, etc.)
        // - Public pages (resource-view, home)
        // - Empty layout pages
        const skipRedirectPaths = [
            '/m/',
            '/kiosk',
            '/sign-in',
            '/sign-up',
            '/sign-out',
            '/forgot-password',
            '/reset-password',
            '/unlock-session',
            '/confirmation-required',
            '/resource-view',
            '/home',
        ];

        const shouldSkip = skipRedirectPaths.some(path => currentUrl.includes(path));
        
        if (shouldSkip) {
            return;
        }

        // Check if user is authenticated (has userData in localStorage)
        const userData = localStorage.getItem('userData');
        if (!userData || userData === 'null' || userData === 'undefined') {
            // Not authenticated yet, don't redirect (let them sign in first)
            return;
        }

        // Redirect to mobile orders page
        console.log('📱 Mobile device detected, redirecting to mobile view');
        this._router.navigate(['/m/orders'], { replaceUrl: true });
    }

    /**
     * Handle kiosk display redirect.
     * Keeps a kiosk display (the 42" panel) locked to the /kiosk surface so
     * the user never sees non-kiosk pages. Kiosk mode is an explicit
     * persisted flag (KioskService). In-app fallback for the pre-boot script
     * + `kioskRedirectGuard` (defense in depth).
     *
     * @private
     */
    private _handleKioskRedirect(): void {
        // Only act when kiosk mode is enabled.
        if (!this._kioskService.isKiosk) {
            return;
        }

        const currentUrl = this._router.url;

        // Leave the kiosk surface and auth / public flows alone.
        const skipRedirectPaths = [
            '/kiosk',
            '/sign-in',
            '/sign-up',
            '/sign-out',
            '/forgot-password',
            '/reset-password',
            '/unlock-session',
            '/confirmation-required',
            '/resource-view',
        ];
        if (skipRedirectPaths.some((path) => currentUrl.includes(path))) {
            return;
        }

        // Only redirect authenticated users.
        const userData = localStorage.getItem('userData');
        if (!userData || userData === 'null' || userData === 'undefined') {
            return;
        }

        console.log('🖥️ Kiosk mode enabled, redirecting to kiosk view');
        this._router.navigate(['/kiosk'], { replaceUrl: true });
    }

    private _shouldPrewarmMcp(): boolean {
        try {
            if (localStorage.getItem('agent1.enabled') !== 'true') {
                return false;
            }

            const authSkipRoutes = [
                '/sign-in',
                '/sign-up',
                '/sign-out',
                '/forgot-password',
                '/reset-password',
                '/unlock-session',
                '/confirmation-required',
            ];
            const currentUrl = this._router.url ?? '';
            if (authSkipRoutes.some((p) => currentUrl.includes(p))) {
                return false;
            }

            const accessToken = String(localStorage.getItem('accessToken') ?? '').trim();
            if (!accessToken) {
                return false;
            }

            const userDataRaw = localStorage.getItem('userData');
            if (!userDataRaw || userDataRaw === 'null' || userDataRaw === 'undefined') {
                return false;
            }

            const userData = JSON.parse(userDataRaw);
            return userData?.role === 'Admin' && !!userData?.id;
        } catch {
            return false;
        }
    }
}
